<script setup lang="ts" generic="IncludePrimitive extends boolean = true, IncludeRelation extends boolean = true, Multiple extends boolean = false">
import type { RowSelectionState } from '@tanstack/vue-table';
import type { CollectionFieldSelectPresentationProps, ModelValueBase } from './CollectionFieldSelect.vue';
import { createColumnHelper } from '@tanstack/vue-table';
import { computed, h } from 'vue';

type ModelValue = ModelValueBase<IncludePrimitive, IncludeRelation, Multiple>;

interface CollectionFieldSelectListProps extends CollectionFieldSelectPresentationProps<Multiple> {
	disabled?: boolean;
}

interface CollectionFieldSelectListSlots {
	item?: (props: { item: any }) => any;
}

defineProps<CollectionFieldSelectListProps>();
const slots = defineSlots<CollectionFieldSelectListSlots>();
const modelValue = defineModel<ModelValue>({ required: true });

const columnHelper = createColumnHelper<any>();
const defaultLabelColumn = columnHelper.accessor('label', {
	header: 'Fields',
	cell: (options) => options.getValue(),
});

const itemColumn = columnHelper.display({
	id: 'item',
	header: 'Fields',
	enableHiding: false,
	enableResizing: false,
	size: 50,
	cell: ({ row }) => h('div', { class: 'flex w-full' }, [slots.item?.({ item: row.original })]),
});

const columns = [slots.item ? itemColumn : defaultLabelColumn];

function handleRowSelectionUpdate(rows: RowSelectionState) {
	modelValue.value = Object.keys(rows) as ModelValue;
}

const rowSelection = computed(() => {
	if (Array.isArray(modelValue.value)) {
		return modelValue.value?.reduce((acc: any, id) => {
			acc[id] = true;
			return acc;
		}, {}) ?? [];
	}
	else {
		return modelValue.value ? { [modelValue.value.toString()]: true } : {};
	}
});
</script>

<template>
	<DDataTable
		:data="items"
		:columns
		:disabled
		can-select
		can-multi-select
		hide-pagination
		:row-selection
		:get-row-id="row => row.id"
		@update:row-selection="handleRowSelectionUpdate"
	/>
</template>
